import Parse from "parse";
import { useEffect, useState } from "react";
import {
  SIV_GoodsReceiptProcess,
  SIV_GoodsReceiptProcessPosition,
  SIV_GoodsReceiptProcessPositionText,
  SIV_Order,
} from "../../../parsegenerated";

export const useGoodsReceiptProcesses = (order?: SIV_Order | null) => {
  const [goodsReceipts, setGoodsReceipts] = useState<SIV_GoodsReceiptProcess[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!order) {
      setGoodsReceipts([]);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const query = new Parse.Query(SIV_GoodsReceiptProcess);
        query.equalTo("order", order);
        query.include("deliveryNote");
        query.include("tenant");
        query.descending("date");
        const results = await query.find();
        setGoodsReceipts(results);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [order?.id]);

  return { goodsReceipts, loading, error };
};

export const useGoodsReceiptProcessPositions = (
  goodsReceiptProcess?: SIV_GoodsReceiptProcess | null,
) => {
  const [positions, setPositions] = useState<SIV_GoodsReceiptProcessPosition[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!goodsReceiptProcess) {
      setPositions([]);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const query = new Parse.Query(SIV_GoodsReceiptProcessPosition);
        query.equalTo("goodsReceiptProcess", goodsReceiptProcess);
        query.include("goodsReceivingItem");
        query.include("tenant");
        query.ascending("positionNo");
        const results = await query.find();
        setPositions(results);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [goodsReceiptProcess?.id]);

  return { positions, loading, error };
};

export const useGoodsReceiptProcessPositionTexts = (
  position?: SIV_GoodsReceiptProcessPosition | null,
) => {
  const [texts, setTexts] = useState<SIV_GoodsReceiptProcessPositionText[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!position) {
      setTexts([]);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const query = new Parse.Query(SIV_GoodsReceiptProcessPositionText);
        query.equalTo("goodsReceiptProcessPosition", position);
        query.ascending("no");
        const results = await query.find();
        setTexts(results);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [position?.id]);

  return { texts, loading, error };
};

export const useGoodsReceiptProcess = (id?: string) => {
  const [goodsReceiptProcess, setGoodsReceiptProcess] =
    useState<SIV_GoodsReceiptProcess | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!id) {
      setGoodsReceiptProcess(null);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const query = new Parse.Query(SIV_GoodsReceiptProcess);
        query.include("order");
        query.include("deliveryNote");
        query.include("tenant");
        const result = await query.get(id);
        setGoodsReceiptProcess(result);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  return { goodsReceiptProcess, loading, error };
};
